import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Avatar,
  AvatarGroup,
  LinearProgress,
  Card,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  Tooltip,
  Grid,
  Divider,
  Collapse,
  TablePagination
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  PriorityHigh as PriorityIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Visibility as ViewIcon,
  FilterAlt as FilterAltIcon,
  FilterAltOff as FilterAltOffIcon,
  ArrowBack as ArrowBackIcon,
  Article as ArticleIcon,
  Task as TaskIcon,
  Folder as ProjectIcon,
  AccountTree as EpicIcon,
  Link as LinkIcon,
  PlaylistAddCheck as StoryTaskIcon,
  BugReport as BugReportIcon,
  Upgrade as UpgradeIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import TaskDetailsDummy from '../../../components/TaskDetailsDummy';

// Epic colors
const epicColors = {
  'Authentication': '#2196f3',
  'Performance': '#10b981',
  'UI/UX': '#8b5cf6',
  'Documentation': '#f59e0b',
  'Security': '#ef4444'
};

// Project details (assuming all tasks belong to the same project)
const projectDetails = {
  id: 1,
  name: "Project Phoenix",
  code: "PHX",
  color: "#2196f3",
  description: "Main project for all development tasks"
};

// Updated mock data with clear task origin
const mockTasks = [
  {
    id: 1,
    name: "Implement user authentication",
    type: "feature",
    origin: "directFromEpic", // Can be: 'directFromEpic', 'fromUserStory'
    epic: "Authentication",
    priority: "high",
    status: "in_progress",
    dueDate: "2024-01-15",
    assignee: [
      { id: 1, name: "John Doe", color: "#ff5722" },
      { id: 2, name: "Sarah Miller", color: "#4caf50" }
    ],
    progress: 65,
    submittedForReview: false,
    // For tasks from user stories
    userStory: null,
    createdFrom: "Epic: Authentication" // Descriptive field
  },
  {
    id: 2,
    name: "Fix login button alignment on mobile",
    type: "bug",
    origin: "fromUserStory", // Created from a user story
    epic: "Authentication",
    priority: "medium",
    status: "todo",
    dueDate: "2024-01-20",
    assignee: [
      { id: 3, name: "Alex Rodriguez", color: "#2196f3" }
    ],
    progress: 0,
    submittedForReview: false,
    // User story details
    userStory: {
      id: "US-101",
      title: "Improve mobile login experience",
      epic: "Authentication"
    },
    createdFrom: "User Story: US-101"
  },
  {
    id: 3,
    name: "Optimize API response caching",
    type: "enhancement",
    origin: "directFromEpic",
    epic: "Performance",
    priority: "low",
    status: "review",
    dueDate: "2024-01-10",
    assignee: [
      { id: 4, name: "Mike Thompson", color: "#9c27b0" },
      { id: 5, name: "Lisa Kim", color: "#ff9800" }
    ],
    progress: 100,
    submittedForReview: true,
    userStory: null,
    createdFrom: "Epic: Performance"
  },
  {
    id: 4,
    name: "Add dark mode toggle",
    type: "feature",
    origin: "fromUserStory",
    epic: "UI/UX",
    priority: "high",
    status: "in_progress",
    dueDate: "2024-01-12",
    assignee: [
      { id: 1, name: "John Doe", color: "#ff5722" },
      { id: 6, name: "Emma Wilson", color: "#e91e63" }
    ],
    progress: 85,
    submittedForReview: false,
    userStory: {
      id: "US-102",
      title: "Implement theme switching",
      epic: "UI/UX"
    },
    createdFrom: "User Story: US-102"
  },
  {
    id: 5,
    name: "Update documentation for new endpoints",
    type: "task",
    origin: "directFromEpic",
    epic: "Documentation",
    priority: "medium",
    status: "done",
    dueDate: "2024-01-05",
    assignee: [
      { id: 2, name: "Sarah Miller", color: "#4caf50" },
      { id: 3, name: "Alex Rodriguez", color: "#2196f3" }
    ],
    progress: 100,
    submittedForReview: true,
    userStory: null,
    createdFrom: "Epic: Documentation"
  },
  {
    id: 6,
    name: "Resolve memory leak in dashboard",
    type: "bug",
    origin: "fromUserStory",
    epic: "Performance",
    priority: "high",
    status: "in_progress",
    dueDate: "2024-01-18",
    assignee: [
      { id: 4, name: "Mike Thompson", color: "#9c27b0" }
    ],
    progress: 45,
    submittedForReview: false,
    userStory: {
      id: "US-103",
      title: "Improve dashboard performance",
      epic: "Performance"
    },
    createdFrom: "User Story: US-103"
  },
  {
    id: 7,
    name: "Create API documentation template",
    type: "task",
    origin: "directFromEpic",
    epic: "Documentation",
    priority: "low",
    status: "todo",
    dueDate: "2024-01-25",
    assignee: [
      { id: 5, name: "Lisa Kim", color: "#ff9800" }
    ],
    progress: 0,
    submittedForReview: false,
    userStory: null,
    createdFrom: "Epic: Documentation"
  },
  {
    id: 8,
    name: "Add user profile picture upload",
    type: "feature",
    origin: "fromUserStory",
    epic: "Authentication",
    priority: "medium",
    status: "review",
    dueDate: "2024-01-14",
    assignee: [
      { id: 1, name: "John Doe", color: "#ff5722" },
      { id: 6, name: "Emma Wilson", color: "#e91e63" }
    ],
    progress: 95,
    submittedForReview: true,
    userStory: {
      id: "US-104",
      title: "Enhance user profile features",
      epic: "Authentication"
    },
    createdFrom: "User Story: US-104"
  },
  {
    id: 9,
    name: "Implement password reset functionality",
    type: "feature",
    origin: "directFromEpic",
    epic: "Authentication",
    priority: "high",
    status: "in_progress",
    dueDate: "2024-01-22",
    assignee: [
      { id: 2, name: "Sarah Miller", color: "#4caf50" }
    ],
    progress: 40,
    submittedForReview: false,
    userStory: null,
    createdFrom: "Epic: Authentication"
  },
  {
    id: 10,
    name: "Fix header alignment on tablet view",
    type: "bug",
    origin: "fromUserStory",
    epic: "UI/UX",
    priority: "medium",
    status: "todo",
    dueDate: "2024-01-28",
    assignee: [
      { id: 3, name: "Alex Rodriguez", color: "#2196f3" }
    ],
    progress: 0,
    submittedForReview: false,
    userStory: {
      id: "US-105",
      title: "Improve responsive design",
      epic: "UI/UX"
    },
    createdFrom: "User Story: US-105"
  },
  {
    id: 11,
    name: "Add performance monitoring",
    type: "enhancement",
    origin: "directFromEpic",
    epic: "Performance",
    priority: "medium",
    status: "in_progress",
    dueDate: "2024-01-30",
    assignee: [
      { id: 4, name: "Mike Thompson", color: "#9c27b0" },
      { id: 5, name: "Lisa Kim", color: "#ff9800" }
    ],
    progress: 30,
    submittedForReview: false,
    userStory: null,
    createdFrom: "Epic: Performance"
  },
  {
    id: 12,
    name: "Update API documentation",
    type: "task",
    origin: "fromUserStory",
    epic: "Documentation",
    priority: "low",
    status: "done",
    dueDate: "2024-01-08",
    assignee: [
      { id: 2, name: "Sarah Miller", color: "#4caf50" }
    ],
    progress: 100,
    submittedForReview: true,
    userStory: {
      id: "US-106",
      title: "Document API endpoints",
      epic: "Documentation"
    },
    createdFrom: "User Story: US-106"
  }
];

// Priority styling
const priorityConfig = {
  high: { bgcolor: '#fde7e9', color: '#d32f2f', label: 'High' },
  medium: { bgcolor: '#fff3e0', color: '#f57c00', label: 'Medium' },
  low: { bgcolor: '#e8f5e9', color: '#2e7d32', label: 'Low' }
};

// Status styling
const statusConfig = {
  todo: { bgcolor: '#e3f2fd', color: '#1976d2', label: 'To Do', icon: <UncheckedIcon sx={{ fontSize: '14px', mr: 0.5 }} /> },
  in_progress: { bgcolor: '#f3e5f5', color: '#7b1fa2', label: 'In Progress', icon: <TimeIcon sx={{ fontSize: '14px', mr: 0.5 }} /> },
  review: { bgcolor: '#fff8e1', color: '#ff8f00', label: 'Review', icon: <ViewIcon sx={{ fontSize: '14px', mr: 0.5 }} /> },
  done: { bgcolor: '#e8f5e9', color: '#2e7d32', label: 'Done', icon: <CheckCircleIcon sx={{ fontSize: '14px', mr: 0.5 }} /> }
};

// Type styling
const typeConfig = {
  feature: { icon: <TaskIcon sx={{ fontSize: '14px', mr: 0.5 }} />, label: 'Feature', color: '#1976d2' },
  bug: { icon: <BugReportIcon sx={{ fontSize: '14px', mr: 0.5 }} />, label: 'Bug', color: '#d32f2f' },
  enhancement: { icon: <UpgradeIcon sx={{ fontSize: '14px', mr: 0.5 }} />, label: 'Enhancement', color: '#7b1fa2' },
  task: { icon: <DescriptionIcon sx={{ fontSize: '14px', mr: 0.5 }} />, label: 'Task', color: '#2e7d32' }
};

// Origin styling - How the task was created
const originConfig = {
  directFromEpic: { 
    icon: <EpicIcon sx={{ fontSize: '14px', mr: 0.5 }} />, 
    label: 'From Epic', 
    color: '#ff9800',
    description: 'Created directly from Epic'
  },
  fromUserStory: { 
    icon: <StoryTaskIcon sx={{ fontSize: '14px', mr: 0.5 }} />, 
    label: 'From User Story', 
    color: '#9c27b0',
    description: 'Created from User Story'
  }
};

// Get unique assignees
const allAssignees = Array.from(new Set(mockTasks.flatMap(task => task.assignee.map(a => a.id))))
  .map(id => {
    const assignee = mockTasks.flatMap(task => task.assignee).find(a => a.id === id);
    return assignee;
  })
  .filter(Boolean);

// TaskTable Component Definition
const TaskTable = ({ projectId, epicName }) => {
  const [tasks, setTasks] = useState(mockTasks);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [originFilter, setOriginFilter] = useState('all');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [reviewFilter, setReviewFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesType = typeFilter === 'all' || task.type === typeFilter;
    const matchesOrigin = originFilter === 'all' || task.origin === originFilter;
    
    const matchesAssignee = assigneeFilter.length === 0 || 
      task.assignee.some(assignee => assigneeFilter.includes(assignee.id));
    
    const taskDate = new Date(task.dueDate);
    const matchesStartDate = !startDate || taskDate >= startDate;
    const matchesEndDate = !endDate || taskDate <= endDate;
    
    const matchesReview = reviewFilter === 'all' || 
      (reviewFilter === 'true' && task.submittedForReview) ||
      (reviewFilter === 'false' && !task.submittedForReview);
    
    return matchesSearch && matchesPriority && matchesStatus && matchesType && 
           matchesOrigin && matchesAssignee && 
           matchesStartDate && matchesEndDate && matchesReview;
  });

  // Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get paginated tasks
  const paginatedTasks = filteredTasks.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Get status counts
  const getStatusCounts = () => {
    return {
      total: tasks.length,
      todo: tasks.filter(t => t.status === 'todo').length,
      inProgress: tasks.filter(t => t.status === 'in_progress').length,
      review: tasks.filter(t => t.status === 'review').length,
      done: tasks.filter(t => t.status === 'done').length
    };
  };

  // Get origin counts
  const getOriginCounts = () => {
    return {
      fromEpic: tasks.filter(t => t.origin === 'directFromEpic').length,
      fromUserStory: tasks.filter(t => t.origin === 'fromUserStory').length
    };
  };

  const statusCounts = getStatusCounts();
  const originCounts = getOriginCounts();

  // Check if any filter is active
  const hasActiveFilters = () => {
    return (
      searchTerm !== '' ||
      priorityFilter !== 'all' ||
      statusFilter !== 'all' ||
      typeFilter !== 'all' ||
      originFilter !== 'all' ||
      assigneeFilter.length > 0 ||
      startDate !== null ||
      endDate !== null ||
      reviewFilter !== 'all'
    );
  };

  const handleSearchClear = () => {
    setSearchTerm('');
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setPriorityFilter('all');
    setStatusFilter('all');
    setTypeFilter('all');
    setOriginFilter('all');
    setAssigneeFilter([]);
    setStartDate(null);
    setEndDate(null);
    setReviewFilter('all');
    setPage(0); // Reset to first page when clearing filters
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleViewTask = (taskId) => {
    setSelectedTaskId(taskId);
    setViewMode('details');
  };

  const handleBackToList = () => {
    setViewMode('table');
    setSelectedTaskId(null);
  };

  const handleTaskDeleted = () => {
    if (viewMode === 'details') {
      handleBackToList();
    }
  };

  const StatusCard = ({ label, count, color }) => (
    <Card 
      sx={{ 
        flex: 1, 
        p: 1,
        textAlign: 'center',
        borderRadius: 1.5,
        bgcolor: color,
        minWidth: '100px',
        boxShadow: 'none',
        border: '1px solid rgba(0,0,0,0.08)'
      }}
    >
      <CardContent sx={{ p: '0 !important' }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.25 }}>
          {count}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
          {label}
        </Typography>
      </CardContent>
    </Card>
  );

  // Details view
  if (viewMode === 'details' && selectedTaskId) {
    return (
      <Box sx={{ height: 'calc(100vh - 80px)', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 1 }}>
          <IconButton 
            onClick={handleBackToList}
            sx={{ mr: 1 }}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Back to Tasks
          </Typography>
        </Box>
        
        <TaskDetailsDummy 
          onClose={handleBackToList}
          taskId={selectedTaskId}
          onTaskDeleted={handleTaskDeleted}
        />
      </Box>
    );
  }

  // Table view
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ 
        // height: 'calc(100vh - 80px)', 
        // overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Project Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 1.5,
          flexShrink: 0,
          gap: 1.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ProjectIcon sx={{ 
              fontSize: '20px', 
              color: projectDetails.color,
              bgcolor: `${projectDetails.color}20`,
              p: 0.5,
              borderRadius: 1
            }} />
            <Typography variant="h5" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
              Tasks
            </Typography>
          </Box>
          
          {/* Project Chip */}
          <Chip
            label={projectDetails.name}
            size="small"
            icon={<ProjectIcon sx={{ fontSize: '14px' }} />}
            sx={{
              fontSize: '0.75rem',
              height: 24,
              fontWeight: 500,
              bgcolor: `${projectDetails.color}20`,
              color: projectDetails.color,
              border: `1px solid ${projectDetails.color}40`
            }}
          />
          
          {epicName && (
            <Chip
              label={epicName}
              size="small"
              sx={{
                fontSize: '0.75rem',
                height: 24,
                fontWeight: 500,
                bgcolor: `${epicColors[epicName] || '#2196f3'}20`,
                color: epicColors[epicName] || '#2196f3'
              }}
            />
          )}
          
          {/* Origin Summary */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
            <Chip
              label={`${originCounts.fromEpic} From Epics`}
              size="small"
              icon={<EpicIcon sx={{ fontSize: '12px' }} />}
              sx={{
                fontSize: '0.7rem',
                height: 22,
                bgcolor: '#fff3e0',
                color: '#f57c00'
              }}
            />
            <Chip
              label={`${originCounts.fromUserStory} From User Stories`}
              size="small"
              icon={<StoryTaskIcon sx={{ fontSize: '12px' }} />}
              sx={{
                fontSize: '0.7rem',
                height: 22,
                bgcolor: '#f3e5f5',
                color: '#7b1fa2'
              }}
            />
          </Box>
        </Box>

        {/* Search and Filters Bar */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 1.5,
          flexShrink: 0,
          gap: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <TextField
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{
                flex: 1,
                maxWidth: '300px',
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.8rem',
                  height: '34px'
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: '16px', color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSearchClear} sx={{ p: 0.25 }}>
                      <CloseIcon sx={{ fontSize: '14px' }} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={showFilters ? <FilterAltOffIcon /> : <FilterAltIcon />}
            onClick={toggleFilters}
            sx={{
              fontSize: '0.7rem',
              borderRadius: 1.5,
              minWidth: 'auto',
              px: 1,
              py: 0.5,
              height: '34px'
            }}
          >
            {showFilters ? 'Hide' : 'Filters'}
          </Button>
        </Box>

        {/* Status Cards - Compact */}
        <Box sx={{ mb: 1.5, flexShrink: 0 }}>
          <Stack 
            direction="row" 
            spacing={1} 
            sx={{ 
              flexWrap: 'nowrap',
              overflow: 'hidden'
            }}
          >
            <StatusCard label="Total" count={statusCounts.total} color="#f5f5f5" />
            <StatusCard label="To Do" count={statusCounts.todo} color="#e3f2fd" />
            <StatusCard label="In Progress" count={statusCounts.inProgress} color="#f3e5f5" />
            <StatusCard label="Review" count={statusCounts.review} color="#fff8e1" />
            <StatusCard label="Done" count={statusCounts.done} color="#e8f5e9" />
          </Stack>
        </Box>

        {/* Filters Section - Collapsible */}
        <Collapse in={showFilters} sx={{ flexShrink: 0 }}>
          <Paper sx={{ 
            p: 1.5, 
            mb: 1.5, 
            borderRadius: 1.5, 
            border: '1px solid #e0e0e0',
            overflow: 'hidden'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <FilterAltIcon sx={{ mr: 1, fontSize: '16px', color: 'text.secondary' }} />
              <Typography variant="subtitle1" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                Filters
              </Typography>
              <Box sx={{ flex: 1 }} />
              {hasActiveFilters() && (
                <Button
                  size="small"
                  onClick={clearAllFilters}
                  sx={{ fontSize: '0.65rem', textTransform: 'none', minWidth: 'auto', p: 0.5 }}
                >
                  Clear All
                </Button>
              )}
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            <Grid container spacing={1}>
              {/* Status Filter */}
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All</MenuItem>
                    <MenuItem value="todo" sx={{ fontSize: '0.75rem' }}>To Do</MenuItem>
                    <MenuItem value="in_progress" sx={{ fontSize: '0.75rem' }}>In Progress</MenuItem>                 
                    <MenuItem value="done" sx={{ fontSize: '0.75rem' }}>Done</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Priority Filter */}
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Priority</InputLabel>
                  <Select
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value)}
                    label="Priority"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All</MenuItem>
                    <MenuItem value="high" sx={{ fontSize: '0.75rem' }}>High</MenuItem>
                    <MenuItem value="medium" sx={{ fontSize: '0.75rem' }}>Medium</MenuItem>
                    <MenuItem value="low" sx={{ fontSize: '0.75rem' }}>Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Type Filter */}
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Type</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    label="Type"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All</MenuItem>
                    <MenuItem value="feature" sx={{ fontSize: '0.75rem' }}>Feature</MenuItem>
                    <MenuItem value="bug" sx={{ fontSize: '0.75rem' }}>Bug</MenuItem>
                    <MenuItem value="enhancement" sx={{ fontSize: '0.75rem' }}>Enhancement</MenuItem>
                    <MenuItem value="task" sx={{ fontSize: '0.75rem' }}>Task</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Origin Filter - How task was created */}
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Task Origin</InputLabel>
                  <Select
                    value={originFilter}
                    onChange={(e) => setOriginFilter(e.target.value)}
                    label="Task Origin"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All Origins</MenuItem>
                    <MenuItem value="directFromEpic" sx={{ fontSize: '0.75rem' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <EpicIcon sx={{ fontSize: '14px', color: originConfig.directFromEpic.color }} />
                        Direct from Epic
                      </Box>
                    </MenuItem>
                    <MenuItem value="fromUserStory" sx={{ fontSize: '0.75rem' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StoryTaskIcon sx={{ fontSize: '14px', color: originConfig.fromUserStory.color }} />
                        From User Story
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Submitted for Review Filter */}
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Review</InputLabel>
                  <Select
                    value={reviewFilter}
                    label="Review"
                    onChange={(e) => setReviewFilter(e.target.value)}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All</MenuItem>
                    <MenuItem value="true" sx={{ fontSize: '0.75rem' }}>Submitted</MenuItem>
                    <MenuItem value="false" sx={{ fontSize: '0.75rem' }}>Not Submitted</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Start Date Filter */}
              <Grid item xs={6} md={3}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: { 
                        '& .MuiInputBase-input': { fontSize: '0.75rem' },
                        '& .MuiInputLabel-root': { fontSize: '0.75rem' }
                      }
                    }
                  }}
                />
              </Grid>

              {/* End Date Filter */}
              <Grid item xs={6} md={3}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: { 
                        '& .MuiInputBase-input': { fontSize: '0.75rem' },
                        '& .MuiInputLabel-root': { fontSize: '0.75rem' }
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Collapse>

        {/* Active Filters Display */}
        {hasActiveFilters() && (
          <Box sx={{ mb: 1, flexShrink: 0 }}>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {searchTerm && (
                <Chip
                  label={`"${searchTerm}"`}
                  size="small"
                  onDelete={() => setSearchTerm('')}
                  sx={{ fontSize: '0.6rem', height: 20 }}
                />
              )}
              {priorityFilter !== 'all' && (
                <Chip
                  label={`Priority: ${priorityConfig[priorityFilter].label}`}
                  size="small"
                  onDelete={() => setPriorityFilter('all')}
                  sx={{
                    fontSize: '0.6rem',
                    height: 20,
                    bgcolor: priorityConfig[priorityFilter].bgcolor,
                    color: priorityConfig[priorityFilter].color
                  }}
                />
              )}
              {statusFilter !== 'all' && (
                <Chip
                  label={`Status: ${statusConfig[statusFilter].label}`}
                  size="small"
                  onDelete={() => setStatusFilter('all')}
                  sx={{
                    fontSize: '0.6rem',
                    height: 20,
                    bgcolor: statusConfig[statusFilter].bgcolor,
                    color: statusConfig[statusFilter].color
                  }}
                />
              )}
              {typeFilter !== 'all' && (
                <Chip
                  label={`Type: ${typeConfig[typeFilter].label}`}
                  size="small"
                  onDelete={() => setTypeFilter('all')}
                  sx={{ fontSize: '0.6rem', height: 20 }}
                />
              )}
              {originFilter !== 'all' && (
                <Chip
                  label={originConfig[originFilter].label}
                  size="small"
                  icon={originConfig[originFilter].icon}
                  onDelete={() => setOriginFilter('all')}
                  sx={{
                    fontSize: '0.6rem',
                    height: 20,
                    bgcolor: `${originConfig[originFilter].color}20`,
                    color: originConfig[originFilter].color
                  }}
                />
              )}
              {reviewFilter !== 'all' && (
                <Chip
                  label={`Review: ${reviewFilter === 'true' ? 'Yes' : 'No'}`}
                  size="small"
                  onDelete={() => setReviewFilter('all')}
                  sx={{
                    fontSize: '0.6rem',
                    height: 20
                  }}
                />
              )}
              {assigneeFilter.length > 0 && (
                <Chip
                  label={`Assignee: ${assigneeFilter.length}`}
                  size="small"
                  onDelete={() => setAssigneeFilter([])}
                  sx={{ fontSize: '0.6rem', height: 20 }}
                />
              )}
              {startDate && (
                <Chip
                  label={`From: ${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                  size="small"
                  onDelete={() => setStartDate(null)}
                  sx={{ fontSize: '0.6rem', height: 20 }}
                />
              )}
              {endDate && (
                <Chip
                  label={`To: ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                  size="small"
                  onDelete={() => setEndDate(null)}
                  sx={{ fontSize: '0.6rem', height: 20 }}
                />
              )}
            </Stack>
          </Box>
        )}

        {/* Table Container - Fixed height without scroll */}
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <TableContainer 
            component={Paper} 
            sx={{ 
              flex: 1,
              borderRadius: 1.5,
              border: '1px solid #e0e0e0',
              boxShadow: 'none',
              overflow: 'hidden'
            }}
          >
            <Table>
              <TableHead sx={{ bgcolor: '#fafafa' }}>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 1, width: '30%' }}>Task Name</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 1, width: '15%' }}>Epic & Origin</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 1, width: '10%' }}>Type</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 1, width: '10%' }}>Status</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 1, width: '15%' }}>Progress</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 1, width: '10%' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTasks.map((task) => (
                  <TableRow 
                    key={task.id}
                    sx={{ 
                      height: '55px',
                      '&:hover': { bgcolor: '#f5f5f5' }
                    }}
                  >
                    {/* Task Name with Assignee */}
                    <TableCell sx={{ p: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          {task.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AvatarGroup max={2} sx={{ 
                            '& .MuiAvatar-root': { 
                              width: 18, 
                              height: 18, 
                              fontSize: '8px',
                              border: '1px solid white'
                            }
                          }}>
                            {task.assignee.map((person, index) => (
                              <Avatar
                                key={index}
                                sx={{ bgcolor: person.color }}
                                title={person.name}
                              >
                                {person.name.split(' ').map(n => n[0]).join('')}
                              </Avatar>
                            ))}
                          </AvatarGroup>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Epic & Origin */}
                    <TableCell sx={{ p: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {/* Epic */}
                        <Chip
                          label={task.epic}
                          size="small"
                          sx={{
                            fontSize: '0.65rem',
                            height: 20,
                            bgcolor: `${epicColors[task.epic] || '#2196f3'}20`,
                            color: epicColors[task.epic] || '#2196f3',
                            justifyContent: 'flex-start',
                            width: 'fit-content'
                          }}
                        />
                        
                        {/* Origin with link to User Story if applicable */}
                        <Tooltip title={originConfig[task.origin].description} arrow>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {task.origin === 'fromUserStory' && task.userStory ? (
                              <Chip
                                label={`US: ${task.userStory.id}`}
                                size="small"
                                icon={<LinkIcon sx={{ fontSize: '10px' }} />}
                                onClick={() => {/* Navigate to user story */}}
                                sx={{
                                  fontSize: '0.6rem',
                                  height: 18,
                                  bgcolor: `${originConfig[task.origin].color}20`,
                                  color: originConfig[task.origin].color,
                                  cursor: 'pointer',
                                  '&:hover': {
                                    bgcolor: `${originConfig[task.origin].color}30`
                                  }
                                }}
                              />
                            ) : (
                              <Chip
                                label={originConfig[task.origin].label}
                                size="small"
                                icon={originConfig[task.origin].icon}
                                sx={{
                                  fontSize: '0.6rem',
                                  height: 18,
                                  bgcolor: `${originConfig[task.origin].color}20`,
                                  color: originConfig[task.origin].color
                                }}
                              />
                            )}
                          </Box>
                        </Tooltip>
                      </Box>
                    </TableCell>

                    {/* Type */}
                    <TableCell sx={{ p: 1 }}>
                      <Chip
                        label={typeConfig[task.type].label}
                        size="small"
                        sx={{
                          fontSize: '0.65rem',
                          height: 20,
                          bgcolor: `${typeConfig[task.type].color}20`,
                          color: typeConfig[task.type].color
                        }}
                      />
                    </TableCell>

                    {/* Status */}
                    <TableCell sx={{ p: 1 }}>
                      <Chip
                        label={statusConfig[task.status].label}
                        size="small"
                        icon={statusConfig[task.status].icon}
                        sx={{
                          fontSize: '0.65rem',
                          height: 20,
                          bgcolor: statusConfig[task.status].bgcolor,
                          color: statusConfig[task.status].color
                        }}
                      />
                    </TableCell>

                    {/* Progress with Due Date */}
                    <TableCell sx={{ p: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ flex: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={task.progress}
                              sx={{
                                height: 4,
                                borderRadius: 2,
                                bgcolor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 2,
                                  bgcolor: task.progress === 100 ? '#4caf50' : 
                                           task.progress > 50 ? '#1976d2' : '#ff9800'
                                }
                              }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', minWidth: '25px' }}>
                            {task.progress}%
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <CalendarIcon sx={{ fontSize: '10px', color: 'text.secondary' }} />
                          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                            Due: {new Date(task.dueDate).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={{ p: 1 }}>
                      <Tooltip title="View Details" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleViewTask(task.id)}
                          sx={{
                            p: 0.5,
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                          }}
                        >
                          <ViewIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredTasks.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{
              borderTop: '1px solid #e0e0e0',
              '& .MuiTablePagination-toolbar': {
                minHeight: '52px',
                padding: '0 8px'
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: '0.75rem',
                margin: 0
              }
            }}
          />
        </Box>

        {/* Footer Stats */}
        <Box sx={{ 
          mt: 0.5, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexShrink: 0,
          pt: 0.5,
          borderTop: '1px solid rgba(0,0,0,0.08)'
        }}>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
            Showing {filteredTasks.length} tasks • {projectDetails.name}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
            {originCounts.fromEpic} Direct from Epics • {originCounts.fromUserStory} From User Stories
          </Typography>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

// Export the component
export default TaskTable;